// @flow
import * as React from 'react';
import { compose, withState, withHandlers } from 'recompose';
import Radium from 'radium';
import { connect } from 'react-redux';
import DateTimeField from 'react-datetime';
import {
  Card,
  CardHeader,
  CardBlock,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';

import type { SurveyType } from 'actions/surveys';
import { createSurvey, loadSurveys } from 'actions/surveys';

import OptionsSelector from 'components/OptionsSelector';
import ImageUpload from 'components/ImageUpload';

type Props = {
  // recompose
  title: string,
  onTitleChange: (title: string) => void,
  description: string,
  onDescriptionChange: (description: string) => void,
  answers: Array<string>,
  onAnswersChange: (answers: Array<string>) => void,
  handleAnswerChange: (index: number, answer: string) => void,
  endsAt: number,
  onEndsAtChange: (endsAt: number) => void,
  visibleIn: Array<string>,
  onVisibleInChange: (communityIds: Array<string>) => void,
  image: ?string,
  onImageChange: (image: string) => void,
  type: SurveyType,
  onTypeChange: (type: SurveyType) => void,

  // redux
  communities: { [communityId: string]: string },
  error: ?string,
  onCreateSurvey: typeof createSurvey,
  onLoadSurveys: typeof loadSurveys,
};

class NewSurvey extends React.Component<Props> {
  _onSubmitSurvey = async () => {
    const {
      title,
      description,
      answers,
      endsAt,
      onCreateSurvey,
      onLoadSurveys,
      onTitleChange,
      onDescriptionChange,
      onAnswersChange,
      visibleIn,
      onVisibleInChange,
      image,
      onImageChange,
      type,
      onTypeChange,
    } = this.props;

    try {
      await onCreateSurvey(
        title,
        description,
        answers.map(a => a.trim()).filter(a => a.length > 0),
        endsAt,
        visibleIn,
        image,
        type
      );
      await onLoadSurveys();

      onTitleChange('');
      onDescriptionChange('');
      onAnswersChange(['']);
      onVisibleInChange([]);
      onImageChange('');
      onTypeChange('closed');
    } catch (e) {
      // TODO
      console.error(e);
    }
  };

  render() {
    const {
      title,
      onTitleChange,
      description,
      onDescriptionChange,
      answers,
      handleAnswerChange,
      endsAt,
      onEndsAtChange,
      communities,
      onVisibleInChange,
      image,
      onImageChange,
      type,
      onTypeChange,
      error,
    } = this.props;

    return (
      <Card style={styles.root}>
        <CardHeader style={styles.title}>Neue Umfrage erstellen</CardHeader>
        <CardBlock>
          <FormGroup>
            <Input
              type="textfield"
              placeholder="Titel der Umfrage ..."
              value={title}
              onChange={event => onTitleChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Input
              type="textarea"
              placeholder="Beschreibung der Umfrage ..."
              value={description}
              onChange={event => onDescriptionChange(event.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Art der Umfrage</Label>
            <FormGroup>
              <Label check>
                <Input
                  type="radio"
                  name="type"
                  checked={type === 'closed'}
                  onChange={() => onTypeChange('closed')}
                />{' '}
                geschlossen
              </Label>{' '}
              <Label check>
                <Input
                  type="radio"
                  name="type"
                  checked={type === 'open'}
                  onChange={() => onTypeChange('open')}
                />{' '}
                offen
              </Label>
            </FormGroup>
          </FormGroup>
          {type === 'closed' && (
            <FormGroup>
              <Label>Antwortmöglichkeiten</Label>
              {answers.map((answer, index) => (
                <Input
                  key={`answer-${index}`}
                  style={{ marginBottom: 8 }}
                  type="textfield"
                  placeholder={`Antwort ${index + 1}`}
                  onChange={event =>
                    handleAnswerChange(index, event.target.value)
                  }
                />
              ))}
              <Button
                color="link"
                style={styles.link}
                size="sm"
                onClick={() => handleAnswerChange(answers.length, '')}
              >
                Weitere Antwort hinzufügen
              </Button>
            </FormGroup>
          )}
          <FormGroup>
            <Label>Ende der Umfrage</Label>
            <DateTimeField
              onChange={moment => onEndsAtChange(moment.unix() * 1000)}
            />
          </FormGroup>
          <FormGroup>
            <Label>Bild hinzufügen</Label>
            <ImageUpload image={image} onImageSelected={onImageChange} />
          </FormGroup>
          <OptionsSelector
            label="Auswahl der Gemeinden"
            options={communities}
            onOptionsSelected={onVisibleInChange}
          />
          {!!error && (
            <Alert color="danger">
              {error}: Konnte Thema nicht anlegen. Bitte Support kontaktieren.
            </Alert>
          )}
          <Button
            color="primary"
            disabled={
              !title ||
              !description ||
              (type === 'closed' &&
                answers.filter(a => a.trim().length > 0).length === 0) ||
              endsAt < +new Date()
            }
            onClick={this._onSubmitSurvey}
          >
            Umfrage erstellen
          </Button>
        </CardBlock>
      </Card>
    );
  }
}

const styles = {
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
  },
  link: {
    fontSize: 12,
    textAlign: 'center',
    cursor: 'pointer',
  },
};

const mapStateToProps = ({
  auth: {
    user: { communities },
  },
  surveys: { error },
}) => ({
  communities,
  error: (error && error.code) || null,
});
const mapDispatchToProps = dispatch => ({
  onCreateSurvey: (...args) => dispatch(createSurvey(...args)),
  onLoadSurveys: (...args) => dispatch(loadSurveys(...args)),
});
const NewSurveyContainer = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withState('title', 'onTitleChange', ''),
  withState('description', 'onDescriptionChange', ''),
  withState('type', 'onTypeChange', 'closed'),
  withState('answers', 'onAnswersChange', ['']),
  withState('endsAt', 'onEndsAtChange', +new Date()),
  withState('visibleIn', 'onVisibleInChange', []),
  withState('image', 'onImageChange', null),
  withHandlers({
    handleAnswerChange: ({ answers, onAnswersChange }) => (
      index: number,
      answer: string
    ) => {
      const newAnswers = [...answers];
      newAnswers[index] = answer;
      onAnswersChange(newAnswers);
    },
  }),
  Radium
)(NewSurvey);

export default NewSurveyContainer;



// WEBPACK FOOTER //
// ./src/views/Surveys/NewSurvey.js